// src/hooks/useMealReminder.js

import { useEffect, useCallback, useRef } from "react";
import { App, Button } from "antd";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import { useMealMate } from "../../context/MealMateContext";

// Extend dayjs with plugins
dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);

const useMealReminder = () => {
  const { notification: antdNotification } = App.useApp();
  const { user, isAuthenticated, loading: userLoading } = useMealMate();
  const notificationCheckInterval = useRef(null);

  const notificationSentToday = useRef({});

  const getMealTypeName = (mealType) => {
    switch (mealType) {
      case "breakfast":
        return "bữa sáng";
      case "lunch":
        return "bữa trưa";
      case "dinner":
        return "bữa tối";
      case "snack":
        return "bữa ăn nhẹ";
      default:
        return "";
    }
  };

  const showAntdNotification = useCallback(
    (mealType, mealTypeName) => {
      const key = `meal-reminder-${mealType}`;
      antdNotification.open({
        message: `🔔 Đã đến giờ ${mealTypeName}!`,
        description: `Đã đến lúc chuẩn bị cho bữa ${mealTypeName} của bạn.`,
        duration: 0, // 0 có nghĩa là thông báo sẽ không tự động đóng
        key: key,
        btn: (
          <Button
            type="primary"
            size="small"
            onClick={() => antdNotification.destroy(key)} // Đóng thông báo khi click
          >
            Đã hiểu
          </Button>
        ),
        onClick: () => {},
        onClose: () => {},
      });
    },
    [antdNotification]
  );

  const checkAndShowMealReminder = useCallback(
    (mealReminder, currentMoment) => {
      if (!mealReminder || !mealReminder.enabled) {
        return;
      }

      const mealType = mealReminder.mealType;
      const reminderMinutes = mealReminder.reminderMinutes; // Phút trước giờ ăn để nhắc nhở

      const today = currentMoment.format("YYYY-MM-DD");

      if (notificationSentToday.current.lastResetDate !== today) {
        notificationSentToday.current = { lastResetDate: today };
      }

      if (notificationSentToday.current[mealType] === today) {
        return;
      }

      // Đảm bảo mealReminder.time là một đối tượng dayjs hợp lệ
      let reminderTimeDayjs;
      if (dayjs.isDayjs(mealReminder.time)) {
        reminderTimeDayjs = mealReminder.time;
      } else if (
        typeof mealReminder.time === "string" &&
        mealReminder.time.match(/^\d{2}:\d{2}$/)
      ) {
        reminderTimeDayjs = dayjs(mealReminder.time, "HH:mm");
      } else {
        return;
      }

      const triggerTime = currentMoment
        .set("hour", reminderTimeDayjs.hour())
        .set("minute", reminderTimeDayjs.minute())
        .set("second", 0)
        .set("millisecond", 0);

      const notificationTriggerMoment = triggerTime.subtract(
        reminderMinutes,
        "minute"
      );

      const notificationWindowStart =
        notificationTriggerMoment.startOf("minute"); // Bắt đầu từ phút đó
      const notificationWindowEnd = notificationTriggerMoment
        .add(2, "minute")
        .endOf("minute"); // Kết thúc 2 phút sau

      // Kiểm tra nếu thời gian hiện tại nằm trong cửa sổ kích hoạt
      if (
        currentMoment.isBetween(
          notificationWindowStart,
          notificationWindowEnd,
          "second",
          "[]"
        )
      ) {
        if (!("Notification" in window)) {
          console.warn("Trình duyệt này không hỗ trợ thông báo.");
          return;
        }

        if (Notification.permission === "granted") {
          // Quyền đã được cấp, hiển thị thông báo
          showAntdNotification(mealType, getMealTypeName(mealType));
        } else if (Notification.permission !== "denied") {
          // Quyền chưa được cấp hoặc bị từ chối trước đó, yêu cầu người dùng
          Notification.requestPermission().then((permission) => {
            if (permission === "granted") {
              showAntdNotification(mealType, getMealTypeName(mealType));
            } else {
              console.warn("Người dùng đã từ chối quyền thông báo.");
              // Hiển thị thông báo Ant Design thay thế nếu quyền trình duyệt bị từ chối
              antdNotification.warning({
                message: `Không thể hiển thị thông báo cho ${getMealTypeName(
                  mealType
                )}`,
                description:
                  "Vui lòng cho phép trình duyệt hiển thị thông báo để nhận nhắc nhở.",
                duration: 8,
              });
            }
          });
        } else {
          console.warn("Người dùng đã từ chối quyền thông báo vĩnh viễn.");
          antdNotification.warning({
            message: `Thông báo ${getMealTypeName(mealType)} bị chặn`,
            description:
              "Bạn đã từ chối quyền thông báo của trình duyệt. Vui lòng thay đổi trong cài đặt trình duyệt.",
            duration: 8,
          });
        }

        // Đánh dấu là đã gửi thông báo cho bữa ăn này trong ngày
        notificationSentToday.current[mealType] = today;
      } else {
      }
    },
    [antdNotification, showAntdNotification]
  );

  useEffect(() => {
    // Hàm callback cho setInterval
    const intervalCallback = () => {
      if (
        !isAuthenticated ||
        userLoading ||
        !user ||
        !user.notificationSettings ||
        !user.notificationSettings.enabled
      ) {
        return;
      }

      // Lấy thời gian hiện tại
      const currentMoment = dayjs();

      user.notificationSettings.mealReminders.forEach((mealReminder) => {
        const isMealReminderTypeEnabled =
          user.notificationSettings.types?.includes("meal_reminder");
        if (isMealReminderTypeEnabled && mealReminder.enabled) {
          checkAndShowMealReminder(mealReminder, currentMoment);
        } else {
        }
      });
    };

    if (notificationCheckInterval.current) {
      clearInterval(notificationCheckInterval.current);
    }

    intervalCallback();

    notificationCheckInterval.current = setInterval(
      intervalCallback,
      30 * 1000
    ); // Kiểm tra mỗi 30 giây

    return () => {
      if (notificationCheckInterval.current) {
        clearInterval(notificationCheckInterval.current);
        notificationCheckInterval.current = null;
      }
    };
  }, [user, isAuthenticated, userLoading, checkAndShowMealReminder]);

  return null;
};

export default useMealReminder;
